import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import {
  Camera, Upload, Wand2, Crop, Loader2, CheckCircle2, AlertCircle, Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HeadshotManager({ player, onUpdate }) {
  const { isDark } = useTheme();
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropCoords, setCropCoords] = useState({ x: 0, y: 0, width: 300, height: 400 });
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleUpload = useCallback(async (file) => {
    if (!file || !player?.id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/headshots/upload/${player.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showMsg('Photo uploaded successfully!');
      if (onUpdate) onUpdate(data.headshot_url);
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }, [player, onUpdate]);

  const handleAIProcess = async () => {
    if (!player?.id) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/headshots/process/${player.id}`);
      if (data.success) {
        showMsg('AI processing complete!');
        if (onUpdate) onUpdate(data.processed_url);
      } else {
        showMsg(data.message || 'AI processing returned no output', 'error');
      }
    } catch (err) {
      showMsg(err.response?.data?.detail || 'AI processing failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualCrop = async () => {
    if (!player?.id) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/headshots/manual-crop/${player.id}`, cropCoords);
      if (data.success) {
        showMsg('Crop applied!');
        setShowCropDialog(false);
        if (onUpdate) onUpdate(data.cropped_url);
      } else {
        showMsg('Crop failed', 'error');
      }
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Crop failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const headshotUrl = player?.headshot_url ? `${API_URL}${player.headshot_url}` : null;

  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} data-testid="headshot-manager">
      <CardHeader className="pb-2">
        <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
          <Camera className="h-4 w-4 text-amber-500" />
          Player Photo
          {player?.headshot_processed && <Badge className="bg-green-600 text-xs ml-2">Processed</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {message && (
          <div className={`mb-3 p-2 rounded-lg text-sm flex items-center gap-2 ${message.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/30' : 'bg-green-500/10 text-green-400 border border-green-500/30'}`}>
            {message.type === 'error' ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {message.text}
          </div>
        )}

        <div className="flex items-start gap-4">
          {/* Photo Preview */}
          <div className={`w-24 h-32 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden ${isDark ? 'border-slate-600 bg-slate-700/30' : 'border-slate-300 bg-slate-50'}`}>
            {headshotUrl ? (
              <img src={headshotUrl} alt={`${player?.first_name} headshot`} className="w-full h-full object-cover" data-testid="headshot-preview" />
            ) : (
              <Image className={`h-8 w-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            )}
          </div>

          {/* Actions */}
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap gap-2">
              {/* File Upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                data-testid="headshot-file-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={isDark ? 'border-slate-600 text-slate-300' : ''}
                data-testid="upload-photo-btn"
              >
                {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                Upload
              </Button>

              {/* Camera Capture */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className={isDark ? 'border-slate-600 text-slate-300' : ''}
                data-testid="capture-photo-btn"
              >
                <Camera className="h-3 w-3 mr-1" />Camera
              </Button>
            </div>

            {/* AI + Manual Crop (only if photo exists) */}
            {player?.headshot_url && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={handleAIProcess}
                  disabled={processing}
                  data-testid="ai-process-btn"
                >
                  {processing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wand2 className="h-3 w-3 mr-1" />}
                  AI Enhance
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCropDialog(true)}
                  className={isDark ? 'border-slate-600 text-slate-300' : ''}
                  data-testid="manual-crop-btn"
                >
                  <Crop className="h-3 w-3 mr-1" />Manual Crop
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Crop Dialog */}
        <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
          <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Manual Crop</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {headshotUrl && (
                <div className="flex justify-center">
                  <img src={headshotUrl} alt="Original" className="max-h-48 rounded-lg" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {['x', 'y', 'width', 'height'].map(field => (
                  <div key={field}>
                    <label className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{field.toUpperCase()}</label>
                    <input
                      type="number"
                      value={cropCoords[field]}
                      onChange={e => setCropCoords(prev => ({ ...prev, [field]: parseInt(e.target.value) || 0 }))}
                      className={`w-full h-9 rounded-md px-3 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
                      data-testid={`crop-${field}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowCropDialog(false)}>Cancel</Button>
                <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleManualCrop} disabled={processing} data-testid="apply-crop-btn">
                  {processing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Crop className="h-3 w-3 mr-1" />}
                  Apply Crop
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
